import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { SubSink } from "subsink";
import { UserToken } from "../../../../../auth/user-token.model";
import { UserService } from "../../../../../core/user/user.service";
import { ChaseIdComponent } from "../../../../../shared/chase-grid/chase-id.component";
import { BasicGridComponent } from "../../../../../shared/grid/basic-grid/basic-grid.component";
import { GridPipeName } from "../../../../../shared/grid/grid-pipe.enum";
import { GridService } from "../../../../../shared/grid/grid.service";
import { GridColumnDefinition } from "../../../../../shared/grid/models/grid-column-definition.model";
import { GridConfiguration } from "../../../../../shared/grid/models/grid-configuration.model";
import { ArrayHelper } from "../../../../../utilities/contracts/array-helper";
import { OverreadFeedbackFilter } from "../../../member/chase-detail/chase-detail-chart/overread-feedback/overread-feedback-filter";
import { OverreadFeedbackQueryItem } from "../../../member/chase-detail/chase-detail-chart/overread-feedback/overread-feedback-query-item.model";
import { OverreadFeedbackQuerySearch } from "../../../member/chase-detail/chase-detail-chart/overread-feedback/overread-feedback-query-search.model";
import { OverreadFeedbackStatus } from "../../../member/chase-detail/chase-detail-chart/overread-feedback/overread-feedback-status.enum";
import { OverreadFeedbackService } from "../../../member/chase-detail/chase-detail-chart/overread-feedback/overread-feedback.service";
import { CodingReviewMode } from "../../../project/project-config/coding-review-mode.enum";

@Component({
  selector: "app-clinical-orfeedback",
  templateUrl: "./clinical-orfeedback.component.html",
  styleUrls: ["./clinical-orfeedback.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClinicalOrFeedbackComponent implements OnInit, OnDestroy {
  @ViewChild("chaseIdColumn", { static: true }) chaseIdColumn: TemplateRef<ChaseIdComponent>;
  @ViewChild("reviewColumn", { static: true }) reviewColumn: TemplateRef<any>;
  @ViewChild("reviewCompletedCountTemplate", { static: true }) reviewCompletedCountTemplate: TemplateRef<any>;
  @ViewChild("reviewToBeCompletedTemplate", { static: true }) reviewToBeCompletedTemplate: TemplateRef<any>;
  @ViewChild("errorCountTemplate", { static: true }) errorCountTemplate: TemplateRef<any>;
  @Input() chaseIdRouteUrl = "/members/chase/:chaseId";
  private sink = new SubSink();
  private user: UserToken;
  assignedTo: number;
  initializeGridData: OverreadFeedbackQueryItem[] = [];
  gridData: OverreadFeedbackQueryItem[] = [];
  criticalErrorToggle = false;
  nonCriticalErrorToggle = false;
  exportAllBtn = false;
  exportButtonText = "Export All";
  @ViewChild(BasicGridComponent, { static: true }) basicGridComponent: BasicGridComponent;

  constructor(
    private router: Router,
    private overreadFeedbackService: OverreadFeedbackService,
    private userService: UserService,
    private gridService: GridService,
    private changeDetector: ChangeDetectorRef,
    private readonly route: ActivatedRoute
  ) { }

  ngOnInit() {
    this.user = this.userService.getUserToken();
    this.sink.add(
      this.overreadFeedbackService.getOverreadFeedbackList(new OverreadFeedbackQuerySearch())
        .subscribe(data => {
          this.nonCriticalErrorToggle = true;
          if (ArrayHelper.isAvailable(data)) {
            if (this.isManagerGrid) {
              this.initializeGridData = data.filter(x => x.chaseOverreadFeedbackStatus !== OverreadFeedbackStatus.CODING);
              this.gridData = this.initializeGridData;
            } else {
              this.initializeGridData = OverreadFeedbackFilter.forUser(data, this.user.userId);
              this.gridData = this.initializeGridData;
            }
            this.toggleNoncriticalErrors();
            this.nonCriticalErrorToggle = true;
            this.changeDetector.markForCheck();
          }
        })
    );
  }

  ngOnDestroy() {
    this.sink.unsubscribe();
  }

  export() {
    const criticalErrors = OverreadFeedbackFilter.complete(this.initializeGridData, this.user.userId);
    const noncriticalErrors = OverreadFeedbackFilter.incomplete(this.initializeGridData, this.user.userId);
    const allErrors = [...criticalErrors, ...noncriticalErrors];

    this.basicGridComponent.primeGrid.value = allErrors;
    this.basicGridComponent.primeGrid.exportCSV();
    this.basicGridComponent.primeGrid.value = this.gridData;
  }
  selectionChange(event) {
    const ele = document.getElementById("export");
    ele.style.display = "";
    this.exportAllBtn = false;
  }

  get gridConfiguration(): GridConfiguration {
    const columns = [
      new GridColumnDefinition({ field: "chaseId", template: this.chaseIdColumn, header: "Chase ID", width: "135px" }),
      new GridColumnDefinition({ field: "measureCode", header: "Measure" }),
      new GridColumnDefinition({ field: "codingUserName", header: "Abstractor Name", show: this.isManagerGrid }),
      new GridColumnDefinition({ field: "mrrDate", header: " MRR Date and Time Submitted", pipeName: GridPipeName.Date, format: "MM-dd-yyyy hh:mm a", timeZone: "local" }),
      new GridColumnDefinition({ field: "qaUserName", header: "OR Name", show: this.isManagerGrid }),
      new GridColumnDefinition({ field: "overreadDate", header: "ORI Date and Time Submitted", pipeName: GridPipeName.Date, format: "MM-dd-yyyy hh:mm a", timeZone: "local" }),
      new GridColumnDefinition({ field: "overread2Date", header: "OR2 Date and Time Submitted", pipeName: GridPipeName.Date, format: "MM-dd-yyyy hh:mm a", timeZone: "local" }),
      new GridColumnDefinition({ template: this.reviewCompletedCountTemplate, header: "# Of Review Completed", show: false }),
      new GridColumnDefinition({ template: this.reviewToBeCompletedTemplate, header: "# Of Review To Be completed", show: false }),
      new GridColumnDefinition({ field : "errorCount", header: "# Of Errors Found" }),
      new GridColumnDefinition({ field: "chaseOverreadFeedbackStatus", header: "Manager Review", show: this.isManagerGrid }),
      new GridColumnDefinition({ template: this.reviewColumn, isSortableColumn: false, header: "Feedback Status", width: "150px" }),
    ];
    return new GridConfiguration({
      columns,
      pageSize: 25,
      showExportAction: true,
      showActionColumn: false,
      selectionMode: "multiple",
      pageSizeOptions: [10, 25, 50, 100],
    });
  }

  navigateToFeedback(rowData: OverreadFeedbackQueryItem): void {
    const url = this.gridService.generateLink(
      new GridColumnDefinition({ routeUrl: this.chaseIdRouteUrl } as any),
      rowData
    );
    this.router.navigateByUrl(`/${url}`);
  }

  toggleCriticalErrors(): void {
    this.criticalErrorToggle = !this.criticalErrorToggle;
    this.nonCriticalErrorToggle = false;
    this.gridData = OverreadFeedbackFilter.complete(this.initializeGridData, this.user.userId);
    const ele = document.getElementById("export");
    ele.style.display = "none";
    this.exportAllBtn = true;
  }

  toggleNoncriticalErrors(): void {
    this.nonCriticalErrorToggle = !this.nonCriticalErrorToggle;
    this.criticalErrorToggle = false;
    this.gridData = OverreadFeedbackFilter.incomplete(this.initializeGridData, this.user.userId);
    const ele = document.getElementById("export");
    ele.style.display = "none";
    this.exportAllBtn = true;
  }

  isStatusCompleted(rowData: OverreadFeedbackQueryItem): boolean {
    return rowData.chaseOverreadFeedbackStatus === OverreadFeedbackStatus.COMPLETED;
  }

  /**
   * The signed in user submitted this feedback (MRR -> OR1 or OR1 -> OR2) and the coder still has to
   * review/acknowledge it, so there is nothing for the submitter to open yet.
   */
  isAwaitingAcknowledgement(rowData: OverreadFeedbackQueryItem): boolean {
    if (this.isManagerGrid || rowData.qaUserId !== this.user.userId || rowData.codingUserId === this.user.userId) {
      return false;
    }

    const codingAcknowledgmentPending = rowData.codingReceiptStatus === OverreadFeedbackStatus.REQUESTED ||
      rowData.codingReceiptStatus === OverreadFeedbackStatus.REQUESTEDNOTREQUIRED;
    return rowData.chaseOverreadFeedbackStatus === OverreadFeedbackStatus.CODING ||
      (rowData.codingReviewMode === CodingReviewMode.DIAGNOSIS_V3 &&
        rowData.chaseOverreadFeedbackStatus === OverreadFeedbackStatus.COMPLETED &&
        codingAcknowledgmentPending);
  }

  receiptStatus(rowData: OverreadFeedbackQueryItem): string {
    const requested = rowData.codingReceiptStatus === OverreadFeedbackStatus.REQUESTED || rowData.qaReceiptStatus === OverreadFeedbackStatus.REQUESTED;
    return requested ? "Feedback Sent" : "Accepted";
  }

  reviewButtonText(rowData: OverreadFeedbackQueryItem): string {
    const requested = rowData.chaseOverreadFeedbackStatus !== OverreadFeedbackStatus.CODING;
    let status = this.isManagerGrid ? "Review Challenge" : requested ? "Acknowledge" : "Review Feedback";
    if (this.criticalErrorToggle) {
      const received = rowData.chaseOverreadFeedbackStatus === OverreadFeedbackStatus.COMPLETED &&
        ((rowData.codingReceiptStatus === OverreadFeedbackStatus.RECEIVED && rowData.qaReceiptStatus === OverreadFeedbackStatus.REQUESTED) ||
          (rowData.qaReceiptStatus === OverreadFeedbackStatus.RECEIVED && rowData.codingReceiptStatus === OverreadFeedbackStatus.REQUESTED)
          || (rowData.codingReceiptStatus === OverreadFeedbackStatus.RECEIVED && rowData.codingReviewMode === CodingReviewMode.DIAGNOSIS_V3));
      status = received ? "Acknowledged" : status;
    }
    return status;
  }

  get isManagerGrid(): boolean {
    return this.route.snapshot.url[1].path !== "orfeedback";
  }


  getGridData(): OverreadFeedbackQueryItem[] {
    // TODO: Toggle on Error Type when added to DB. Currently toggling complete/incomplete
    const data = this.initializeGridData;
    if (this.criticalErrorToggle) {
      return this.initializeGridData.filter(x => x.chaseOverreadFeedbackStatus === OverreadFeedbackStatus.COMPLETED);
    }

    if (this.nonCriticalErrorToggle) {
      return this.initializeGridData.filter(x => x.chaseOverreadFeedbackStatus !== OverreadFeedbackStatus.COMPLETED);
    }

    return data;
  }

  get criticalErrorBtnClass(): string {
    return this.criticalErrorToggle ? "active-button" : "";
  }

  get nonCriticalErrorBtnClass(): string {
    return this.nonCriticalErrorToggle ? "active-button" : "";
  }
}
